import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaShoppingCart, 
  FaFilter, 
  FaEye, 
  FaUser,
  FaCalendarAlt,
  FaFileAlt,
  FaInfoCircle,
  FaBuilding,
  FaSearch,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
  FaTag,
  FaUserCog,
  FaStore
} from 'react-icons/fa';
import supabase from '../../supabaseClient';
import '../../styles/order-page.css';
import { useAuth } from '../../hooks/useAuth';

const PedidosCarrinho = () => {
  // Estado para armazenar dados
  const [carrinhoItems, setCarrinhoItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  
  // Estados para filtros
  const [usuarios, setUsuarios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [usuarioFilter, setUsuarioFilter] = useState('Todos');
  const [categoriaFilter, setCategoriaFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('Todos');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [valueFilter, setValueFilter] = useState('Todos');
  const [showFilters, setShowFilters] = useState(false);
  const [discountFilter, setDiscountFilter] = useState('Todos');

  // Estados para as abas do modal
  const [activeTab, setActiveTab] = useState('resumo');

  // Cores e estilo consistentes com o design system (mesmo do OrdersList)
  const colors = {
    primary: '#0078D7',
    primaryLight: '#E3F2FD',
    primaryDark: '#005A9E',
    secondary: '#505050',
    success: '#28A745',
    successLight: '#D4EDDA',
    danger: '#DC3545',
    dangerLight: '#F8D7DA',
    warning: '#FFC107',
    warningLight: '#FFF3CD',
    info: '#17A2B8',
    infoLight: '#D1ECF1',
    light: '#F8F9FA',
    dark: '#343A40',
    muted: '#6C757D',
    white: '#FFFFFF',
    border: '#DEE2E6',
    shadow: 'rgba(0, 0, 0, 0.1)'
  };

  // Estilos para o modal flutuante (mantidos do original)
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '1000px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.white,
      borderRadius: '12px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: 'none',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.border} 100%)`,
      borderBottom: `1px solid ${colors.border}`,
      padding: '1.25rem 1.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: colors.white,
      color: colors.muted,
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: `0 2px 5px ${colors.shadow}`
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: colors.dark,
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    tabsContainer: {
      display: 'flex',
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.light,
      padding: '0 1.5rem',
      flexShrink: 0
    },
    tab: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1rem 1.5rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: colors.muted,
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s ease',
      position: 'relative',
      backgroundColor: 'transparent'
    },
    activeTab: {
      color: colors.primary,
      borderBottomColor: colors.primary,
      backgroundColor: colors.white,
      fontWeight: '600'
    },
    tabContent: {
      padding: '0',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.25rem 1.5rem',
      borderTop: `1px solid ${colors.border}`,
      background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.border} 100%)`,
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    cancelButton: {
      border: `1px solid ${colors.border}`,
      background: colors.white,
      color: colors.secondary,
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: colors.dark,
      color: colors.white,
      boxShadow: `0 4px 10px ${colors.shadow}`
    },
    section: {
      margin: '0',
      padding: '1.5rem',
      borderBottom: `1px solid ${colors.border}`
    },
    sectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: colors.dark,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem'
    },
    infoLabel: {
      fontSize: '0.8rem',
      color: colors.muted,
      marginBottom: '0.25rem'
    },
    infoValue: {
      fontSize: '0.95rem',
      fontWeight: '500',
      color: colors.dark
    },
    discountBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem 0.65rem',
      borderRadius: '50px',
      fontSize: '0.75rem',
      fontWeight: '600',
      backgroundColor: colors.successLight,
      color: colors.success
    }
  };

  // Configuração das abas
  const tabs = [
    { id: 'resumo', label: 'Resumo', icon: <FaFileAlt /> },
    { id: 'usuario', label: 'Dados do Expositor', icon: <FaUser /> }
  ];

  // Função para mudar de aba
  const changeTab = (tab) => {
    setActiveTab(tab);
  };

  // Carregar dados do Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        if (isAdmin) {
          await Promise.all([
            fetchCarrinhoItems(),
            fetchUsuarios(),
            fetchCategorias()
          ]);
        } else {
          // Redirecionar se não for admin
          setError('Acesso negado. Esta página é restrita a administradores.');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setError('Falha ao carregar os dados necessários.');
        setLoading(false);
      }
    };

    if (user?.id) {
      loadData();
    }

    // Adicionar estilos CSS para animações (mesmo do OrdersList)
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(30px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .btn-submit:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }
      
      .action-button {
        padding: 0.75rem 1.5rem;
        border-radius: 50px;
        background-color: #212529;
        color: white;
        font-weight: 600;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        transition: all 0.3s ease;
        border: none;
        cursor: pointer;
      }

      .action-button:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }

      .view-button {
        padding: 0.6rem 1.2rem;
        border-radius: 50px;
        background-color: white;
        color: #495057;
        font-weight: 600;
        border: 1px solid #dee2e6;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        transition: all 0.3s ease;
        cursor: pointer;
      }

      .view-button:hover {
        background-color: #f8f9fa;
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      }

      .modal-close-btn:hover {
        background-color: #f8f9fa;
        color: #212529;
        transform: rotate(90deg);
      }
      
      .btn-cancel:hover {
        background-color: #f8f9fa;
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
      }
      
      .btn-submit:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }

      .tab:hover {
        background-color: ${colors.white};
        color: ${colors.primary};
      }

      .position-relative .position-absolute {
        pointer-events: none;
      }

      @media (max-width: 768px) {
        .d-flex.justify-content-end {
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .d-flex.justify-content-end .btn {
          width: 100%;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, [isAdmin, user]);

  // Funções de fetch
  const fetchCarrinhoItems = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_carrinho_pedido')
        .select(`
          *,
          caex_produtos!inner(
            nome, 
            descricao, 
            preco, 
            categoria_id,
            imagem_url,
            estoque
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      // Buscar informações dos usuários
      const userIds = [...new Set(data.map(item => item.usuario_id))];
      const { data: usersData, error: usersError } = await supabase
        .from('caex_expositores')
        .select('usuario_id, nome_empresa, responsavel, email, telefone')
        .in('usuario_id', userIds);

      if (usersError) {
        console.error('Erro ao buscar usuários:', usersError);
      }

      const formattedItems = data.map(item => {
        const usuario = usersData?.find(u => u.usuario_id === item.usuario_id);
        const precoOriginal = parseFloat(item.preco_original) || 0;
        const precoCustomizado = parseFloat(item.preco_customizado) || precoOriginal;
        const desconto = parseFloat(item.desconto) || 0;
        const quantidade = parseInt(item.quantidade) || 1;
        
        // Calcular preço final baseado no tipo de desconto
        let precoFinal = precoCustomizado;
        if (desconto > 0) {
          if (item.tipo_desconto === 'percentage') {
            precoFinal = precoCustomizado * (1 - desconto / 100);
          } else {
            precoFinal = precoCustomizado - desconto;
          }
        }
        
        const totalItem = precoFinal * quantidade;
        
        return {
          id: item.id,
          usuarioId: item.usuario_id,
          produtoId: item.produto_id,
          nomeUsuario: usuario?.nome_empresa || 'Usuário não encontrado',
          responsavel: usuario?.responsavel || '',
          emailUsuario: usuario?.email || '',
          telefoneUsuario: usuario?.telefone || '',
          nomeProduto: item.caex_produtos.nome,
          descricaoProduto: item.caex_produtos.descricao,
          categoriaProduto: item.caex_produtos.categoria_id,
          imagemProduto: item.caex_produtos.imagem_url,
          estoqueProduto: item.caex_produtos.estoque,
          quantidade: quantidade,
          precoOriginal: precoOriginal,
          precoCustomizado: precoCustomizado,
          desconto: desconto,
          tipoDesconto: item.tipo_desconto,
          precoFinal: precoFinal,
          totalItem: totalItem,
          hasDiscount: desconto > 0,
          dadosExtras: item.dados_extras,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        };
      });
      
      setCarrinhoItems(formattedItems);
    } catch (error) {
      console.error('Erro ao carregar itens do carrinho:', error);
      setError(`Não foi possível carregar os itens do carrinho: ${error.message}`);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_expositores')
        .select('usuario_id, nome_empresa')
        .eq('ativo', true)
        .order('nome_empresa', { ascending: true });
      
      if (error) throw error;
      setUsuarios(data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const fetchCategorias = async () => {
    try {
      const { data, error } = await supabase
        .from('caex_categorias')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome', { ascending: true });
      
      if (error) throw error;
      setCategorias(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const viewItemDetails = (item) => {
    setCurrentItem(item);
    setActiveTab('resumo');
    setShowDetailsModal(true);
  };

  // Funções auxiliares
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para limpar todos os filtros
  const clearAllFilters = () => {
    setUsuarioFilter('Todos');
    setCategoriaFilter('Todos');
    setDateFilter('Todos');
    setStartDate('');
    setEndDate('');
    setValueFilter('Todos');
    setDiscountFilter('Todos');
    setSearchTerm('');
  };

  // Função para verificar se há filtros ativos
  const hasActiveFilters = () => {
    return usuarioFilter !== 'Todos' ||
           categoriaFilter !== 'Todos' ||
           dateFilter !== 'Todos' ||
           startDate !== '' ||
           endDate !== '' ||
           valueFilter !== 'Todos' ||
           discountFilter !== 'Todos' ||
           searchTerm.trim() !== '';
  };

  // Função para obter resumo dos filtros ativos
  const getActiveFiltersText = () => {
    const active = [];
    if (usuarioFilter !== 'Todos') {
      const usuario = usuarios.find(u => u.usuario_id === usuarioFilter);
      active.push(`Usuário: ${usuario?.nome_empresa || usuarioFilter}`);
    }
    if (categoriaFilter !== 'Todos') {
      const categoria = categorias.find(c => c.id.toString() === categoriaFilter);
      active.push(`Categoria: ${categoria?.nome || categoriaFilter}`);
    }
    if (dateFilter !== 'Todos') active.push(`Período: ${dateFilter}`);
    if (startDate && endDate) active.push(`Data: ${startDate} até ${endDate}`);
    if (valueFilter !== 'Todos') active.push(`Valor: ${valueFilter}`);
    if (discountFilter !== 'Todos') active.push(`Desconto: ${discountFilter}`);
    if (searchTerm.trim()) active.push(`Busca: "${searchTerm}"`);
    
    return active.join(' • ');
  };

  // Função melhorada para aplicar filtros
  const applyFilters = (items) => {
    let filtered = items;

    // Filtro por usuário
    if (usuarioFilter !== 'Todos') {
      filtered = filtered.filter(item => item.usuarioId === usuarioFilter);
    }

    // Filtro por categoria
    if (categoriaFilter !== 'Todos') {
      filtered = filtered.filter(item => item.categoriaProduto === parseInt(categoriaFilter));
    }

    // Filtro por data
    if (dateFilter !== 'Todos') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdAt);
        const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
        
        switch (dateFilter) {
          case 'Hoje':
            return itemDateOnly.getTime() === today.getTime();
          case 'Última semana':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return itemDateOnly >= weekAgo;
          case 'Último mês':
            const monthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
            return itemDateOnly >= monthAgo;
          case 'Últimos 3 meses':
            const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
            return itemDateOnly >= threeMonthsAgo;
          default:
            return true;
        }
      });
    }

    // Filtro por data personalizada
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= start && itemDate <= end;
      });
    }

    // Filtro por valor
    if (valueFilter !== 'Todos') {
      filtered = filtered.filter(item => {
        const value = item.totalItem;
        switch (valueFilter) {
          case 'Até R$ 50':
            return value <= 50;
          case 'R$ 51 - R$ 200':
            return value > 50 && value <= 200;
          case 'R$ 201 - R$ 500':
            return value > 200 && value <= 500;
          case 'Acima de R$ 500':
            return value > 500;
          default:
            return true;
        }
      });
    }

    // Filtro por desconto
    if (discountFilter !== 'Todos') {
      filtered = filtered.filter(item => {
        switch (discountFilter) {
          case 'Com desconto':
            return item.hasDiscount;
          case 'Sem desconto':
            return !item.hasDiscount;
          default:
            return true;
        }
      });
    }

    // Filtro por busca
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => 
        item.nomeProduto.toLowerCase().includes(term) ||
        item.nomeUsuario.toLowerCase().includes(term) ||
        item.responsavel?.toLowerCase().includes(term) ||
        item.descricaoProduto?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  // Renderizar conteúdo da aba ativa
  const renderTabContent = () => {
    if (!currentItem) return null;

    switch (activeTab) {
      case 'resumo':
        return (
          <div style={modalStyles.section}>
            <h3 style={modalStyles.sectionTitle}>
              <FaFileAlt style={{color: colors.muted}} />
              Resumo do Item
            </h3>
            
            <div style={modalStyles.grid}>
              <div>
                <p style={modalStyles.infoLabel}>Produto</p>
                <p style={modalStyles.infoValue}>{currentItem.nomeProduto}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Quantidade</p>
                <p style={modalStyles.infoValue}>{currentItem.quantidade} unidades</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Preço Original</p>
                <p style={modalStyles.infoValue}>{formatCurrency(currentItem.precoOriginal)}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Preço Final</p>
                <p style={modalStyles.infoValue}>{formatCurrency(currentItem.precoFinal)}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Total do Item</p>
                <p style={modalStyles.infoValue}>{formatCurrency(currentItem.totalItem)}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Adicionado em</p>
                <p style={modalStyles.infoValue}>{formatDate(currentItem.createdAt)}</p>
              </div>
            </div>

            {currentItem.hasDiscount && (
              <div style={{marginTop: '1.5rem'}}>
                <h4 style={{...modalStyles.sectionTitle, fontSize: '1rem', marginBottom: '0.5rem'}}>
                  <FaTag style={{color: colors.success}} />
                  Desconto Aplicado
                </h4>
                <div style={modalStyles.grid}>
                  <div>
                    <p style={modalStyles.infoLabel}>Tipo de Desconto</p>
                    <p style={modalStyles.infoValue}>
                      {currentItem.tipoDesconto === 'percentage' ? 'Percentual' : 'Valor Fixo'}
                    </p>
                  </div>
                  <div>
                    <p style={modalStyles.infoLabel}>Desconto</p>
                    <p style={modalStyles.infoValue}>
                      {currentItem.tipoDesconto === 'percentage' 
                        ? `${currentItem.desconto}%` 
                        : formatCurrency(currentItem.desconto)}
                    </p>
                  </div>
                  <div>
                    <p style={modalStyles.infoLabel}>Economia Total</p>
                    <p style={{...modalStyles.infoValue, color: colors.success}}>
                      {formatCurrency((currentItem.precoOriginal - currentItem.precoFinal) * currentItem.quantidade)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {currentItem.descricaoProduto && (
              <div style={{marginTop: '1.5rem'}}>
                <h4 style={{...modalStyles.sectionTitle, fontSize: '1rem', marginBottom: '0.5rem'}}>
                  <FaInfoCircle style={{color: colors.muted}} />
                  Descrição do Produto
                </h4>
                <p style={{fontSize: '0.9rem', color: colors.secondary, lineHeight: '1.4'}}>
                  {currentItem.descricaoProduto}
                </p>
              </div>
            )}
          </div>
        );

      case 'usuario':
        return (
          <div style={modalStyles.section}>
            <h3 style={modalStyles.sectionTitle}>
              <FaUser style={{color: colors.muted}} />
              Dados do Expositor
            </h3>
            
            {/* Primeira linha: Empresa e Responsável */}
            <div style={modalStyles.grid}>
              <div>
                <p style={modalStyles.infoLabel}>Empresa</p>
                <p style={modalStyles.infoValue}>{currentItem.nomeUsuario}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Responsável</p>
                <p style={modalStyles.infoValue}>{currentItem.responsavel || 'Não informado'}</p>
              </div>
            </div>
            
            {/* Segunda linha: Email (campo completo) */}
            <div style={{marginTop: '1rem'}}>
              <div>
                <p style={modalStyles.infoLabel}>Email</p>
                <p style={{
                  ...modalStyles.infoValue,
                  wordBreak: 'break-all',
                  fontSize: '0.9rem',
                  padding: '0.5rem',
                  backgroundColor: colors.light,
                  borderRadius: '4px',
                  border: `1px solid ${colors.border}`
                }}>
                  {currentItem.emailUsuario || 'Não informado'}
                </p>
              </div>
            </div>
            
            {/* Terceira linha: Telefone */}
            <div style={{marginTop: '1rem'}}>
              <div>
                <p style={modalStyles.infoLabel}>Telefone</p>
                <p style={{
                  ...modalStyles.infoValue,
                  fontSize: '0.95rem'
                }}>
                  {currentItem.telefoneUsuario || 'Não informado'}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const filteredItems = applyFilters(carrinhoItems);

  // Render loading state
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaShoppingCart className="dashboard-title-icon" />
                Pedidos no Carrinho
              </h1>
              <p className="dashboard-subtitle">
                Visualize e gerencie todos os itens que estão nos carrinhos dos expositores
              </p>
            </div>
          </div>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3">Carregando itens do carrinho...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaShoppingCart className="dashboard-title-icon" />
                Pedidos no Carrinho
              </h1>
              <p className="dashboard-subtitle">
                Visualize e gerencie todos os itens que estão nos carrinhos dos expositores
              </p>
            </div>
          </div>
          <div className="alert alert-danger">
            <h5>Erro ao carregar dados</h5>
            <p>{error}</p>
            <button 
              className="btn btn-outline-primary mt-3"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaShoppingCart className="dashboard-title-icon" />
              Pedidos no Carrinho
            </h1>
            <p className="dashboard-subtitle">
              Visualize e gerencie todos os itens que estão nos carrinhos dos expositores
            </p>
          </div>
        </div>

        {/* Botões de ação */}
        <div className="d-flex justify-content-end mb-4">
          <button 
            className="btn btn-outline-secondary me-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? <FaTimes className="me-2" /> : <FaFilter className="me-2" />}
            {showFilters ? 'Ocultar Filtros' : 'Filtros'}
          </button>
        </div>

        {/* Área de filtros */}
        {showFilters && (
          <div className="modern-table-card mb-4">
            <div className="modern-card-body">
              {/* Resumo dos Filtros Ativos */}
              {hasActiveFilters() && (
                <div className="alert alert-info mb-3">
                  <FaInfoCircle className="me-2" />
                  <strong>Filtros ativos:</strong> {getActiveFiltersText()}
                </div>
              )}

              {/* Filtros Básicos */}
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Usuário</label>
                    <select
                      className="form-select"
                      value={usuarioFilter}
                      onChange={(e) => setUsuarioFilter(e.target.value)}
                    >
                      <option value="Todos">Todos os usuários</option>
                      {usuarios.map((usuario) => (
                        <option key={usuario.usuario_id} value={usuario.usuario_id}>
                          {usuario.nome_empresa}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Categoria</label>
                    <select
                      className="form-select"
                      value={categoriaFilter}
                      onChange={(e) => setCategoriaFilter(e.target.value)}
                    >
                      <option value="Todos">Todas as categorias</option>
                      {categorias.map((categoria) => (
                        <option key={categoria.id} value={categoria.id}>
                          {categoria.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Desconto</label>
                    <select
                      className="form-select"
                      value={discountFilter}
                      onChange={(e) => setDiscountFilter(e.target.value)}
                    >
                      <option value="Todos">Todos</option>
                      <option value="Com desconto">Com desconto</option>
                      <option value="Sem desconto">Sem desconto</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Buscar</label>
                    <div className="position-relative">
                      <input 
                        type="text" 
                        className="form-control"
                        placeholder="Produto, usuário, responsável..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros Avançados */}
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Período</label>
                    <select
                      className="form-select"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                    >
                      <option value="Todos">Todos os períodos</option>
                      <option value="Hoje">Hoje</option>
                      <option value="Última semana">Última semana</option>
                      <option value="Último mês">Último mês</option>
                      <option value="Últimos 3 meses">Últimos 3 meses</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Data Início</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Data Fim</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Faixa de Valor</label>
                    <select
                      className="form-select"
                      value={valueFilter}
                      onChange={(e) => setValueFilter(e.target.value)}
                    >
                      <option value="Todos">Todos os valores</option>
                      <option value="Até R$ 50">Até R$ 50</option>
                      <option value="R$ 51 - R$ 200">R$ 51 - R$ 200</option>
                      <option value="R$ 201 - R$ 500">R$ 201 - R$ 500</option>
                      <option value="Acima de R$ 500">Acima de R$ 500</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Botão para limpar filtros */}
              <div className="row">
                <div className="col-12">
                  <div className="d-flex justify-content-end">
                    {hasActiveFilters() && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={clearAllFilters}
                      >
                        <FaTimes className="me-2" />
                        Limpar Filtros
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabela de itens do carrinho */}
        <div className="modern-table-card" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <div className="modern-card-header">
            <h5 className="modern-card-title">
              <FaShoppingCart className="card-title-icon" />
              Itens no Carrinho {filteredItems.length > 0 && `(${filteredItems.length})`}
            </h5>
          </div>
          <div className="modern-card-body">
            <div className="modern-table-container">
              {filteredItems.length === 0 ? (
                <div className="empty-state-container">
                  <FaShoppingCart className="empty-state-icon" />
                  <h3 className="empty-state-title">
                    {hasActiveFilters() ? 'Nenhum item encontrado' : 'Nenhum item no carrinho'}
                  </h3>
                  <p className="empty-state-message">
                    {hasActiveFilters() 
                      ? 'Não foram encontrados itens com os filtros aplicados. Tente ajustar os filtros.'
                      : 'Não há itens nos carrinhos dos expositores no momento.'}
                  </p>
                  {hasActiveFilters() && (
                    <button onClick={clearAllFilters} className="action-button">
                      <FaTimes />
                      Limpar Filtros
                    </button>
                  )}
                </div>
              ) : (
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Usuário</th>
                      <th>Quantidade</th>
                      <th>Preço Unit.</th>
                      <th>Total</th>
                      <th>Adicionado em</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div>
                            <div style={{fontWeight: '500'}}>{item.nomeProduto}</div>
                            {item.hasDiscount && (
                              <span style={modalStyles.discountBadge}>
                                <FaTag size={10} />
                                {item.tipoDesconto === 'percentage' 
                                  ? `${item.desconto}%` 
                                  : formatCurrency(item.desconto)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{item.nomeUsuario}</td>
                        <td className="text-center">{item.quantidade}</td>
                        <td>
                          {item.hasDiscount ? (
                            <div>
                              <span style={{textDecoration: 'line-through', color: colors.muted, fontSize: '0.85rem'}}>
                                {formatCurrency(item.precoOriginal)}
                              </span>
                              <br />
                              <span style={{color: colors.success, fontWeight: '600'}}>
                                {formatCurrency(item.precoFinal)}
                              </span>
                            </div>
                          ) : (
                            formatCurrency(item.precoFinal)
                          )}
                        </td>
                        <td className="order-value">{formatCurrency(item.totalItem)}</td>
                        <td className="order-date">{formatDate(item.createdAt)}</td>
                        <td className="text-right">
                          <button 
                            onClick={() => viewItemDetails(item)}
                            className="view-button"
                          >
                            <FaEye />
                            Ver detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Modal de detalhes do item com sistema de abas */}
        {showDetailsModal && currentItem && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  <FaShoppingCart style={{color: colors.muted}} />
                  Detalhes do Item do Carrinho
                </h2>
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              {/* Sistema de Abas */}
              <div style={modalStyles.tabsContainer}>
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    style={{
                      ...modalStyles.tab,
                      ...(activeTab === tab.id ? modalStyles.activeTab : {})
                    }}
                    className="tab"
                    onClick={() => changeTab(tab.id)}
                  >
                    {tab.icon}
                    {tab.label}
                  </div>
                ))}
              </div>
              
              <div style={modalStyles.tabContent} className="modal-scrollable-body">
                {renderTabContent()}
              </div>
              
              <div style={modalStyles.footer}>
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  className="btn-cancel"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PedidosCarrinho;